import { useDispatch, useSelector } from 'react-redux';

import { useEffect, useRef } from 'react';
import { RootState } from '../ducks/store';
import { useGetTeamsSlice } from './useGetTeamsSlice';
import { useGetCourseNames } from './useGetCourseNames';
import { getCourseIdsFromCourseNames } from '../utils/reports';
import { setInitialAutoLoadPending } from '../ducks/report';

/**
 * Fetches courses and stores active course names and course ids for selected teams to the Redux store on load
 */
export const useFetchCoursesOnLoad = () => {
  const initialCoursesFetch = useRef(false); // Used to prevent the execution of fetchCourses after the initial load

  // ===========================================================================
  // Redux
  // ===========================================================================

  const dispatch = useDispatch();

  const { activeTeams, activeDeselectedCourses } = useSelector(
    (state: RootState) => state.teamsSlice,
  );

  // ===========================================================================
  // Hooks
  // ===========================================================================

  const { fetchCourses } = useGetCourseNames();
  const { setActiveCourseNames, setCurrentTeamCourseIds } = useGetTeamsSlice();

  // ===========================================================================
  // Use Effects
  // ===========================================================================

  // Fetch courses and store active course names and course ids for selected teams to the Redux store
  useEffect(() => {
    if (initialCoursesFetch.current) {
      return;
    }

    // Arm the pre-batch filter lock as soon as we know an auto-load will fire this visit (teams are
    // preselected). Runs on every mount, so return visits to the report are covered too.
    if (activeTeams.length) {
      dispatch(setInitialAutoLoadPending(true));
    }

    fetchCourses({ teamIds: activeTeams })
      .then(({ data }) => {
        const courseIds = getCourseIdsFromCourseNames(data);
        const courseNames =
          data
            ?.filter((course) => !activeDeselectedCourses.includes(course._id))
            .map((course) => course.title) || [];

        setCurrentTeamCourseIds(courseIds);
        setActiveCourseNames(courseNames);

        initialCoursesFetch.current = true;

        // If no course is actively selected — an empty team, all courses deselected, or a fetch that
        // yielded no data — the report batch never runs, so no batch* terminal will release the lock.
        // Release it here, using the same "nothing selected" predicate the batch trigger skips on.
        const activeCourseIds = courseIds.filter((id) => !activeDeselectedCourses.includes(id));
        if (!activeCourseIds.length) {
          dispatch(setInitialAutoLoadPending(false));
        }
      })
      .catch((error) => {
        // Defensive: the RTK lazy trigger resolves rather than rejects, but if it ever throws we
        // log it (this is an obscure failure class that would otherwise be invisible) and still
        // release the lock so the filters can't be left frozen.
        console.error('useFetchCoursesOnLoad: initial course fetch threw unexpectedly', error);
        initialCoursesFetch.current = true;
        dispatch(setInitialAutoLoadPending(false));
      });

    // We don't want to run this effect every time the setter functions change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeams, activeDeselectedCourses, dispatch]);
};
